import { useState } from "react";

interface NewSpeciesModalProps {
  onClose: () => void;
}

export default function NewSpeciesModal({ onClose }: NewSpeciesModalProps) {
  const [form, setForm] = useState({
    popularName: "",
    scientificName: "",
    family: "",
    origin: "nativa",
    size: "médio",
    functionalGroup: "",
    woodDensity: "",
    carbonFactor: "",
    isProtected: false,
    isInvasive: false,
    isFruitful: false,
    isOrnamental: false,
    observations: "",
  });

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl border border-background-200/70 shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-background-200/70 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h3 className="text-base font-semibold text-foreground-900 font-heading">Nova Espécie</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-600 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Nome popular *</label>
              <input type="text" value={form.popularName} onChange={(e) => handleChange("popularName", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="Ex: Ipê Amarelo" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Nome científico *</label>
              <input type="text" value={form.scientificName} onChange={(e) => handleChange("scientificName", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none italic" placeholder="Ex: Handroanthus chrysotrichus" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Família botânica *</label>
              <select value={form.family} onChange={(e) => handleChange("family", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
                <option value="">Selecione...</option>
                <option value="Bignoniaceae">Bignoniaceae</option>
                <option value="Fabaceae">Fabaceae</option>
                <option value="Myrtaceae">Myrtaceae</option>
                <option value="Arecaceae">Arecaceae</option>
                <option value="Anacardiaceae">Anacardiaceae</option>
                <option value="Meliaceae">Meliaceae</option>
                <option value="Malvaceae">Malvaceae</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Origem</label>
              <select value={form.origin} onChange={(e) => handleChange("origin", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
                <option value="nativa">Nativa</option>
                <option value="exótica">Exótica</option>
                <option value="exótica invasora">Exótica invasora</option>
                <option value="ornamental">Ornamental</option>
                <option value="frutífera">Frutífera</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Porte</label>
              <select value={form.size} onChange={(e) => handleChange("size", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
                <option value="pequeno">Pequeno</option>
                <option value="médio">Médio</option>
                <option value="grande">Grande</option>
                <option value="palmeira">Palmeira</option>
                <option value="arbustivo">Arbustivo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Grupo funcional</label>
              <input type="text" value={form.functionalGroup} onChange={(e) => handleChange("functionalGroup", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="Ex: Nativa ornamental" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Densidade da madeira (g/cm³)</label>
              <input type="number" step="0.01" value={form.woodDensity} onChange={(e) => handleChange("woodDensity", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="0,00" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Fator carbono preliminar</label>
              <input type="number" step="0.01" value={form.carbonFactor} onChange={(e) => handleChange("carbonFactor", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none" placeholder="0,00" />
            </div>
          </div>

          <div className="flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isProtected} onChange={(e) => handleChange("isProtected", e.target.checked)} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400" />
              <span className="text-sm text-foreground-700">Espécie protegida</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isInvasive} onChange={(e) => handleChange("isInvasive", e.target.checked)} className="w-4 h-4 rounded border-background-300 text-danger-500 focus:ring-danger-400" />
              <span className="text-sm text-foreground-700">Exótica invasora</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isFruitful} onChange={(e) => handleChange("isFruitful", e.target.checked)} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400" />
              <span className="text-sm text-foreground-700">Frutífera</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isOrnamental} onChange={(e) => handleChange("isOrnamental", e.target.checked)} className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400" />
              <span className="text-sm text-foreground-700">Ornamental</span>
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Observações técnicas</label>
            <textarea value={form.observations} onChange={(e) => handleChange("observations", e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none resize-none" placeholder="Notas e observações sobre a espécie..." />
          </div>
        </div>
        <div className="border-t border-background-200/70 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">Cancelar</button>
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-save-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Salvar espécie
          </button>
        </div>
      </div>
    </div>
  );
}